import { H2, P, Code, CodeBlock, DocTable, Ol, RiskBadge } from '../../components/docs/primitives';
import { SPEC } from './links';

const MANIFEST_EXAMPLE = `id          = "yourhandle.my-plugin"
name        = "My Plugin"
version     = "1.0.0"
author      = "yourhandle"
tier        = "A"
description = "One or two plain sentences about what it does."
homepage    = "https://github.com/yourhandle/my-plugin"
host_min    = "8.0.0"

[runtime]
kind      = "process"
entry     = "my-plugin.exe"
args      = []
transport = "stdio"

[capabilities]
events       = ["on_watch_tick", "on_followed_live"]
host_methods = ["get_followed_live", "notify", "log"]
credentials  = []
network      = "none"
ui           = []`;

export default function Manifest() {
  return (
    <>
      <P>
        <Code>plugin.toml</Code> sits at the root of every plugin and is the complete,
        machine-readable statement of what the plugin is and what it may ask the host for.
        Anything not declared here is denied at runtime.
      </P>
      <CodeBlock code={MANIFEST_EXAMPLE} />

      <H2 id="top-level">Top-level fields</H2>
      <DocTable
        head={['Field', 'Constraints']}
        rows={[
          [
            <Code>id</Code>,
            <>
              Reverse-DNS, globally unique, max 64 chars, never changes across versions. The{' '}
              <Code>app.streamnook.</Code> prefix is reserved for first-party plugins.
            </>,
          ],
          [<Code>name</Code>, 'Display name, max 40 chars.'],
          [<Code>version</Code>, 'Semver (MAJOR.MINOR.PATCH).'],
          [<Code>author</Code>, 'Author handle as listed in the index.'],
          [
            <Code>tier</Code>,
            '"A", "B", or "C". Declared by the author, verified by the index curator; a plugin may not self-certify into a lower tier.',
          ],
          [<Code>description</Code>, 'Plain-language summary, max 200 chars, shown in the install dialog.'],
          [<Code>homepage</Code>, 'Optional https URL.'],
          [<Code>host_min</Code>, 'Minimum StreamNook version. Install is blocked on older hosts.'],
        ]}
      />

      <H2 id="runtime">Runtime</H2>
      <DocTable
        head={['Field', 'Constraints']}
        rows={[
          [
            <Code>kind</Code>,
            '"process" in v1. "wasm" is reserved and rejected by the v1 host.',
          ],
          [
            <Code>entry</Code>,
            'Path of the executable, relative to the plugin directory. No ".." and not absolute.',
          ],
          [<Code>args</Code>, 'Arguments passed at spawn. Optional, default empty.'],
          [
            <Code>transport</Code>,
            '"stdio" in v1. "socket" is reserved and rejected by the v1 host.',
          ],
        ]}
      />

      <H2 id="capabilities">Capabilities</H2>
      <P>
        The whole permission contract. The consent dialog renders this block in plain language,
        line by line, so request only what you use: a plugin asking for more than it plausibly
        needs reads exactly how you would expect.
      </P>
      <DocTable
        head={['Field', 'Meaning']}
        rows={[
          [<Code>events</Code>, 'Events the plugin may subscribe to.'],
          [
            <Code>host_methods</Code>,
            <>
              Host methods the plugin may call. <Code>get_credential</Code> is governed by{' '}
              <Code>credentials</Code>, not this list.
            </>,
          ],
          [
            <Code>credentials</Code>,
            <>
              Credential kinds the host may hand over, each behind its own first-use prompt. v1
              vocabulary: <Code>"twitch.android"</Code>.
            </>,
          ],
          [
            <Code>network</Code>,
            '"none" or "external". The host cannot firewall a separate process, so this is a public declaration to the user, and a plugin observed breaking it is delisted.',
          ],
          [<Code>ui</Code>, <>UI contributions. v1 vocabulary: <Code>"panel"</Code>.</>],
        ]}
      />
      <P>
        Unknown capability strings fail install on v1 hosts (fail closed). Users can grant a
        subset of what you request; <Code>initialize</Code> tells you what you actually got.
      </P>

      <H2 id="tiers">Capability tiers</H2>
      <DocTable
        head={['Value', 'Badge', 'Covers', 'Listed in']}
        rows={[
          [
            <Code>"A"</Code>,
            <RiskBadge tier="A" />,
            'Official APIs, local-only features, read-only integrations, ordinary user actions.',
            'Official index',
          ],
          [
            <Code>"B"</Code>,
            <RiskBadge tier="B" />,
            'Unofficial transport or private APIs used the way a normal viewer would.',
            'Official index',
          ],
          [
            <Code>"C"</Code>,
            <RiskBadge tier="C" />,
            'Background automation, or add-ons that change how playback is resolved.',
            'Community indexes only, behind explicit consent',
          ],
        ]}
      />

      <H2 id="validation">Validation at install</H2>
      <P>In order, failing closed at the first mismatch, with no partial installs:</P>
      <Ol>
        <li>Artifact hash matches the index entry.</li>
        <li>Detached signature verifies against the pinned author key.</li>
        <li>Manifest parses and every constraint above holds.</li>
        <li>
          Manifest <Code>id</Code>, <Code>version</Code>, and <Code>tier</Code> exactly match the
          index entry.
        </li>
        <li>
          <Code>host_min</Code> is satisfied; <Code>runtime.kind</Code> and{' '}
          <Code>runtime.transport</Code> are supported.
        </li>
        <li>The consent dialog is shown and the user grants capabilities.</li>
        <li>The plugin is unpacked into its own directory and registered.</li>
      </Ol>
      <P>
        Full constraints live in{' '}
        <a href={SPEC.manifest} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          MANIFEST.md
        </a>{' '}
        and the consent vocabulary in{' '}
        <a href={SPEC.capabilities} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          CAPABILITIES.md
        </a>
        .
      </P>
    </>
  );
}
